import type React from "react";
import type {ContractDocument, ContractVersion} from "../contractExplorerTypes.ts";
import {endBasisLabel, formatDate, typeLabel} from "../contractExplorerFormat.ts";
import {CollapsibleSection} from "./CollapsibleSection.tsx";
import {SourceCite} from "./SourceCite.tsx";

type Props = {
    versions: ContractVersion[];
    inForce: number | null;
    shown: number;
    documents: Record<string, ContractDocument>;
    onShow: (versionNo: number) => void;
};

/** The bundle's periods: each version, where its start comes from, what made it and how much changed. */
export const VersionHistory: React.FC<Props> = ({versions, inForce, shown, documents, onShow}) => {
    if (!versions.length) return null;
    return (
        <CollapsibleSection title={`Version history (${versions.length})`}
                            sub="Each version is the whole bundle for one period, built from the documents that make it.">
            <div className="ci-card ci-versions">
                {versions.map(v => (
                    <div key={v.version_no} className={`ci-ver${v.version_no === shown ? ' is-shown' : ''}`}>
                        <span className="ci-ver-n">v{v.version_no}</span>
                        <div className="ci-ver-p">
                            <span>
                                {formatDate(v.valid_from)} → {v.valid_to ? formatDate(v.valid_to) : 'open'}
                                {v.version_no === inForce && <span className="ci-change">in force</span>}
                            </span>
                            <span className="ci-csub">
                                {v.from_basis ? `Starts on ${typeLabel(v.from_basis).toLowerCase()}` : 'Undated'}
                                {' · '}{endBasisLabel(v.to_basis)}
                                {v.version_no > 0 && ` · ${v.changed} ${v.changed === 1 ? 'value' : 'values'} changed`}
                                {' · from '}
                                {v.documents.map(id => <SourceCite key={id} documentId={id} documents={documents}/>)}
                            </span>
                            {v.warnings.map(w => <span key={w} className="ci-ver-w">{w}</span>)}
                        </div>
                        {v.version_no !== shown && (
                            <button type="button" className="ci-link" onClick={() => onShow(v.version_no)}>Show</button>
                        )}
                    </div>
                ))}
            </div>
        </CollapsibleSection>
    );
};
